import { AlertTriangle } from 'lucide-react';
import { formatDateMMDDYY } from '../../../utils/applicationFormatters';
import { formatAmount } from '@/lib/utils/formatters';
import { formatAddressDisplay } from '@/features/applicant/utils/addressHelpers';
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent';
import {
  anyDistanceRequiresExplanation,
} from '@/lib/utils/employmentDistanceService';
import { cn } from '@lib/utils/helpers';
import styles from '../../../styles/applicationDetail.module.css';
import {
  EMPLOYMENT_TYPES,
  EMPLOYMENT_TYPES_VIEW,
  INCOME_PERIODS,
  CHECK_TYPES,
} from '../../../constants';
import {
  angularJobIncomeYearly,
  formatEmployerAddressAngular,
  monthsAtJobDisplay,
  parseJobDistance,
} from './incomeEmploymentHelpers';

import type { EmploymentJobApiRow, IncomeAttachmentFile } from "../../../types/incomeEmploymentApi";

export function EmploymentView({
  employmentData,
  employmentType,
  applicationData,
  onPreviewFile,
}: {
  employmentData: EmploymentJobApiRow;
  employmentType: string;
  applicationData?: Record<string, unknown>;
  applicationAllData?: Record<string, unknown>;
  onPreviewFile: (
    file: IncomeAttachmentFile,
    allFiles: IncomeAttachmentFile[],
  ) => void | Promise<void>;
}) {
  if (!employmentData) return null;

  const jobTypeKey = employmentData.job_type || employmentData.employment_type;
  const typeLabel =
    (jobTypeKey && EMPLOYMENT_TYPES_VIEW[String(jobTypeKey)]) ||
    (jobTypeKey && EMPLOYMENT_TYPES[String(jobTypeKey)]) ||
    jobTypeKey ||
    "";

  const employerName =
    employmentData.employer_name || employmentData.company_name || "";
  const employerAddress =
    formatEmployerAddressAngular(employmentData) ||
    formatAddressDisplay({
      street:
        employmentData.employer_street_address ||
        employmentData.company_address ||
        employmentData.employer_street,
      city: employmentData.company_city || employmentData.employer_city,
      state: employmentData.company_state || employmentData.employer_state,
      zip_code:
        employmentData.company_zipcode || employmentData.employer_zip_code,
      country: employmentData.employer_country || "US",
    }) ||
    "";

  const rawIncome = employmentData.income ?? employmentData.monthly_income;
  const periodKey = employmentData.income_period || "Per_Month";
  const incomeLine =
    rawIncome != null && String(rawIncome).trim() !== ""
      ? `${formatAmount(rawIncome)} / ${INCOME_PERIODS[periodKey] || periodKey}`
      : "";

  const yearlyNum = angularJobIncomeYearly(rawIncome, periodKey);
  const yearlyLine = yearlyNum > 0 ? formatAmount(yearlyNum) : "";

  const monthsAtJob = monthsAtJobDisplay(employmentData);

  const startRaw = employmentData.job_start_date || employmentData.start_date;
  const endRaw = employmentData.job_end_date || employmentData.end_date;
  const startDisplay = formatDateMMDDYY(startRaw);
  const endDisplay = formatDateMMDDYY(endRaw);

  const hrEmail = employmentData.hr_manager_contact_email;

  const jobDistance = parseJobDistance(employmentData.job_distance);
  const applicantToPropertyDistance = parseJobDistance(
    applicationData?.applicant_to_property_distance,
  );
  const applicantToEmployerDistance = parseJobDistance(
    (employmentData as Record<string, unknown>).applicant_to_employer_distance,
  );
  const hasDistanceExplanation = Boolean(
    String(employmentData.distance_explain ?? "").trim(),
  );
  const hasDistanceWarning =
    employmentType === "current_job" &&
    (hasDistanceExplanation ||
      anyDistanceRequiresExplanation(
        jobDistance || null,
        applicantToPropertyDistance || null,
        applicantToEmployerDistance || null,
      ));

  const proofKey =
    employmentType === "current_job"
      ? "current_proof_of_income"
      : "previous_proof_of_income";
  const proofList: IncomeAttachmentFile[] = Array.isArray(employmentData[proofKey])
    ? employmentData[proofKey]
    : [];

  const isSelfEmployed =
    employmentData.job_type === "SELF_EMPLOYED" ||
    employmentData.employment_type === "SELF_EMPLOYED";

  return (
    <div>
      {/* Row 1 — Type | Employer Name | Employer Address */}
      <div className={styles.infoRow}>
        <div className={styles.infoColumn}>
          <h6>Type</h6>
          <p>{typeLabel || ""}</p>
        </div>
        <div className={styles.infoColumn}>
          <h6>Employer Name</h6>
          <p>{employerName}</p>
        </div>
        <div className={styles.infoColumn}>
          <h6>Employer Address</h6>
          <p>{employerAddress}</p>
        </div>
      </div>

      {/* Row 2 — Income | Yearly Calculated Income | Number of months at this job */}
      <div className={styles.infoRow}>
        <div className={styles.infoColumn}>
          <h6>Income</h6>
          <p>{incomeLine}</p>
        </div>
        <div className={styles.infoColumn}>
          <h6>Yearly Calculated Income</h6>
          <p>{yearlyLine}</p>
        </div>
        <div className={styles.infoColumn}>
          <h6>Number of months at this job</h6>
          <p>{monthsAtJob}</p>
        </div>
      </div>

      {/* Row 3 — Job Start Date | Job End Date | HR/Manager Contact Email */}
      <div className={styles.infoRow}>
        <div className={styles.infoColumn}>
          <h6>Job Start Date</h6>
          <p>{startDisplay}</p>
        </div>
        <div className={styles.infoColumn}>
          <h6>Job End Date</h6>
          <p>{endDisplay}</p>
        </div>
        <div className={styles.infoColumn}>
          <h6>HR/Manager Contact Email</h6>
          <p>{hrEmail ?? ""}</p>
        </div>
      </div>

      {/* Previous only — Job Gap | Job Gap Explanation */}
      {employmentData.job_gap && employmentType === "previous_job" && (
        <div className={styles.infoRow}>
          <div className={styles.infoColumn}>
            <h6>Job Gap</h6>
            <p>{employmentData.job_gap}</p>
          </div>
          <div className={styles.infoColumn}>
            <h6>Job Gap Explanation</h6>
            <p>{employmentData.job_gap_explanation ?? ""}</p>
          </div>
        </div>
      )}

      {/* Self-employed — Website */}
      {isSelfEmployed && employmentData.employer_website && (
        <div className={styles.infoRow}>
          <div className={styles.infoColumn}>
            <h6>Website</h6>
            <p>
              <a
                href={employmentData.employer_website}
                target="_blank"
                rel="noopener noreferrer"
              >
                {employmentData.employer_website}
              </a>
            </p>
          </div>
        </div>
      )}

      {/* Distance warning — view mode: Explain in disabled field, Job Distance in alert box */}
      {hasDistanceWarning && (
        <div className={cn(styles.infoRow, styles.incomeDistanceViewRow)}>
          <div className={cn(styles.infoColumn, styles.incomeDistanceExplainColumn)}>
            <h6>Explain</h6>
            <p>{employmentData.distance_explain ?? ""}</p>
          </div>
          <div className={styles.infoColumn}>
            <h6 data-testid="employment-distance-warning-label">Job Distance</h6>
            <div
              className={cn(
                styles.incomeDistanceViewWarningBox,
                styles.incomeDistanceWarningAlert,
              )}
              role="alert"
              data-testid="employment-distance-warning-alert"
            >
              <p className={styles.incomeDistanceViewWarningText}>
                <AlertTriangle
                  className={styles.incomeDistanceViewWarningIcon}
                  aria-hidden="true"
                />
                <span>{APPLICANT_CONTENT.errors.employment.distanceWarning}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Proof of Income — `current_proof_of_income` / `previous_proof_of_income` */}
      {proofList.length > 0 && (
        <div className={styles.infoRow}>
          <div className={styles.infoColumn} style={{ flex: "1 1 100%" }}>
            <h6>Proof of Income</h6>
            <div className={styles.filesList}>
              {proofList.map((file, fileIndex) => (
                <div key={fileIndex} className={styles.fileItem}>
                  {file.filepath ? (
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0 text-left text-inherit hover:underline"
                      onClick={() => {
                        void onPreviewFile(file, proofList);
                      }}
                    >
                      {file.filename}
                      {file.type
                        ? ` - ${CHECK_TYPES[file.type] || file.type}`
                        : ""}
                    </button>
                  ) : (
                    <span>{file.filename}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
